import React, { useState, useEffect, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Text, Box, Sphere, Cylinder } from '@react-three/drei';
import * as THREE from 'three';

interface DanceMove {
  id: string;
  name: string;
  emoji: string;
  duration: number;
  description: string;
}

interface Robot3DProps {
  position: [number, number, number];
  color: string;
  isDancing: boolean;
  danceAnimation?: string;
  name: string;
}

// 3D Robot Component
const Robot3D: React.FC<Robot3DProps> = ({ position, color, isDancing, danceAnimation, name }) => {
  const meshRef = useRef<THREE.Group>(null);

  useFrame((state) => {
    if (meshRef.current && isDancing) {
      // Different dance animations based on danceAnimation type
      switch (danceAnimation) {
        case 'robot-wave':
          meshRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 3) * 0.3;
          meshRef.current.position.y = position[1] + Math.sin(state.clock.elapsedTime * 4) * 0.2;
          break;
        case 'robot-spin':
          meshRef.current.rotation.y += 0.1;
          meshRef.current.scale.setScalar(1 + Math.sin(state.clock.elapsedTime * 2) * 0.2);
          break;
        case 'robot-shuffle':
          meshRef.current.position.x = position[0] + Math.sin(state.clock.elapsedTime * 5) * 0.3;
          meshRef.current.rotation.z = Math.sin(state.clock.elapsedTime * 4) * 0.2;
          break;
        case 'robot-jump':
          meshRef.current.position.y = position[1] + Math.abs(Math.sin(state.clock.elapsedTime * 6)) * 0.4;
          break;
        default:
          // Default bounce animation
          meshRef.current.position.y = position[1] + Math.sin(state.clock.elapsedTime * 2) * 0.1;
          meshRef.current.rotation.y += 0.02;
          break;
      }
    } else if (meshRef.current) {
      // Reset to original position when not dancing
      meshRef.current.position.set(position[0], position[1], position[2]);
      meshRef.current.rotation.set(0, 0, 0);
      meshRef.current.scale.setScalar(1);
    }
  });

  return (
    <group ref={meshRef} position={position}>
      {/* Robot Body */}
      <Box args={[0.8, 1.2, 0.6]} position={[0, 0, 0]}>
        <meshStandardMaterial color={color} metalness={0.7} roughness={0.3} />
      </Box>
      
      {/* Robot Head */}
      <Box args={[0.6, 0.6, 0.6]} position={[0, 0.9, 0]}>
        <meshStandardMaterial color={color} metalness={0.8} roughness={0.2} />
      </Box>
      
      {/* Robot Eyes */}
      <Sphere args={[0.1, 8, 8]} position={[-0.2, 1, 0.3]}>
        <meshStandardMaterial color="#00ff00" emissive="#00ff00" emissiveIntensity={0.5} />
      </Sphere>
      <Sphere args={[0.1, 8, 8]} position={[0.2, 1, 0.3]}>
        <meshStandardMaterial color="#00ff00" emissive="#00ff00" emissiveIntensity={0.5} />
      </Sphere>
      
      {/* Robot Antenna */}
      <Cylinder args={[0.02, 0.02, 0.3]} position={[0, 1.3, 0]} rotation={[0, 0, 0]}>
        <meshStandardMaterial color="#ffff00" />
      </Cylinder>
      <Sphere args={[0.05, 8, 8]} position={[0, 1.45, 0]}>
        <meshStandardMaterial color="#ffff00" emissive="#ffff00" emissiveIntensity={0.3} />
      </Sphere>
      
      {/* Robot Arms */}
      <Box args={[0.2, 0.8, 0.2]} position={[-0.6, 0.2, 0]}>
        <meshStandardMaterial color={color} metalness={0.7} roughness={0.3} />
      </Box>
      <Box args={[0.2, 0.8, 0.2]} position={[0.6, 0.2, 0]}>
        <meshStandardMaterial color={color} metalness={0.7} roughness={0.3} />
      </Box>
      
      {/* Robot Legs */}
      <Box args={[0.2, 0.6, 0.2]} position={[-0.2, -0.9, 0]}>
        <meshStandardMaterial color={color} metalness={0.7} roughness={0.3} />
      </Box>
      <Box args={[0.2, 0.6, 0.2]} position={[0.2, -0.9, 0]}>
        <meshStandardMaterial color={color} metalness={0.7} roughness={0.3} />
      </Box>
      
      {/* Robot Name */}
      <Text
        position={[0, -1.5, 0]}
        fontSize={0.2}
        color="white"
        anchorX="center"
        anchorY="middle"
      >
        {name}
      </Text>
    </group>
  );
};

// Dance Floor Component
const DanceFloor: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const floorRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    if (floorRef.current && isActive) {
      // Pulsing floor effect
      const intensity = Math.sin(state.clock.elapsedTime * 2) * 0.3 + 0.7;
      const material = floorRef.current.material as THREE.MeshStandardMaterial;
      if (material) {
        material.emissiveIntensity = intensity;
      }
    }
  });

  return (
    <mesh ref={floorRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.1, 0]}>
      <planeGeometry args={[20, 20]} />
      <meshStandardMaterial 
        color="#1a1a2e" 
        emissive="#16213e" 
        emissiveIntensity={isActive ? 0.5 : 0.1}
        metalness={0.8}
        roughness={0.2}
      />
    </mesh>
  );
};

// Arena Lights Component
const ArenaLights: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const lightRef = useRef<THREE.PointLight>(null);

  useFrame((state) => {
    if (lightRef.current && isActive) {
      // Color changing lights
      const time = state.clock.elapsedTime;
      lightRef.current.color.setHSL((time * 0.1) % 1, 0.8, 0.6);
      lightRef.current.intensity = Math.sin(time * 2) * 0.5 + 1;
    }
  });

  return (
    <>
      {/* Main arena light */}
      <pointLight 
        ref={lightRef}
        position={[0, 5, 0]} 
        intensity={isActive ? 2 : 1}
        color="#ffffff"
      />
      
      {/* Side lights */}
      <pointLight position={[-8, 3, 0]} intensity={0.5} color="#ff6b6b" />
      <pointLight position={[8, 3, 0]} intensity={0.5} color="#4ecdc4" />
      <pointLight position={[0, 3, -8]} intensity={0.5} color="#45b7d1" />
      <pointLight position={[0, 3, 8]} intensity={0.5} color="#f9ca24" />
      
      {/* Ambient light */}
      <ambientLight intensity={0.3} />
    </>
  );
};

// Particle System for Dance Effects
const DanceParticles: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const particlesRef = useRef<THREE.Points>(null);
  const particleCount = 100;

  useFrame((state) => {
    if (particlesRef.current && isActive) {
      const positions = particlesRef.current.geometry.attributes.position.array as Float32Array;
      
      for (let i = 0; i < particleCount; i++) {
        const i3 = i * 3;
        positions[i3 + 1] += Math.sin(state.clock.elapsedTime + i) * 0.01;
        positions[i3 + 0] += Math.cos(state.clock.elapsedTime + i) * 0.005;
      }
      
      particlesRef.current.geometry.attributes.position.needsUpdate = true;
    }
  });

  if (!isActive) return null;

  const positions = new Float32Array(particleCount * 3);
  for (let i = 0; i < particleCount; i++) {
    positions[i * 3] = (Math.random() - 0.5) * 20;
    positions[i * 3 + 1] = Math.random() * 5;
    positions[i * 3 + 2] = (Math.random() - 0.5) * 20;
  }

  return (
    <points ref={particlesRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[positions, 3]}
        />
      </bufferGeometry>
      <pointsMaterial size={0.1} color="#ffffff" transparent opacity={0.6} />
    </points>
  );
};

// Fallback 2D Arena Component
const FallbackArena: React.FC<{ 
  playerDancing: boolean; 
  opponentDancing: boolean; 
  danceMoves: DanceMove[];
  startPlayerDance: (move: DanceMove) => void;
}> = ({ playerDancing, opponentDancing, danceMoves, startPlayerDance }) => {
  return (
    <div className="w-full h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
      <div className="text-center text-white">
        <h1 className="text-4xl font-bold mb-8">🤖 2D Robot Dance Arena</h1>
        <p className="text-xl mb-8">3D scene unavailable, using 2D fallback</p>
        
        {/* 2D Robot Display */}
        <div className="flex justify-center space-x-16 mb-8">
          {/* Player Robot */}
          <div className="text-center">
            <div className={`w-32 h-32 bg-blue-500 rounded-full flex items-center justify-center text-6xl mb-4 transition-all duration-300 ${
              playerDancing ? 'animate-bounce scale-110' : ''
            }`}>
              🤖
            </div>
            <h3 className="text-xl font-semibold text-blue-400">Your Robot</h3>
            <p className="text-sm">{playerDancing ? 'Dancing!' : 'Ready'}</p>
          </div>
          
          {/* Opponent Robot */}
          <div className="text-center">
            <div className={`w-32 h-32 bg-red-500 rounded-full flex items-center justify-center text-6xl mb-4 transition-all duration-300 ${
              opponentDancing ? 'animate-bounce scale-110' : ''
            }`}>
              🤖
            </div>
            <h3 className="text-xl font-semibold text-red-400">Dance Master</h3>
            <p className="text-sm">{opponentDancing ? 'Dancing!' : 'Ready'}</p>
          </div>
        </div>
        
        {/* Dance Moves */}
        <div className="grid grid-cols-4 gap-4 max-w-2xl mx-auto">
          {danceMoves.map((move) => (
            <button
              key={move.id}
              onClick={() => startPlayerDance(move)}
              disabled={playerDancing}
              className={`p-4 rounded-lg text-sm font-medium transition-all ${
                playerDancing 
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              <div className="text-2xl mb-2">{move.emoji}</div>
              <div className="text-xs">{move.name}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Main 3D Arena Component
const RobotDanceArena3D: React.FC = () => {
  const [playerDancing, setPlayerDancing] = useState(false);
  const [opponentDancing, setOpponentDancing] = useState(false);
  const [playerAnimation, setPlayerAnimation] = useState<string>('');
  const [opponentAnimation, setOpponentAnimation] = useState<string>('');
  const [arenaActive, setArenaActive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [useFallback, setUseFallback] = useState(false);

  const danceMoves: DanceMove[] = [
    { id: '1', name: 'Robot Wave', emoji: '👋', duration: 2, description: 'Wave hello like a robot' },
    { id: '2', name: 'Spin Attack', emoji: '🌪️', duration: 3, description: 'Spin around like a tornado' },
    { id: '3', name: 'Moonwalk', emoji: '🌙', duration: 2, description: 'Slide backwards smoothly' },
    { id: '4', name: 'Robot Shuffle', emoji: '🤖', duration: 2, description: 'Quick side-to-side steps' },
    { id: '5', name: 'Power Pose', emoji: '💪', duration: 1, description: 'Strike a powerful pose' },
    { id: '6', name: 'Breakdance', emoji: '🕺', duration: 4, description: 'Complex floor moves' },
    { id: '7', name: 'Robot Jump', emoji: '🤸', duration: 1, description: 'Jump with robot precision' },
    { id: '8', name: 'Synchronized', emoji: '🔄', duration: 3, description: 'Perfect timing moves' }
  ];

  const startPlayerDance = (move: DanceMove) => {
    setPlayerDancing(true);
    setPlayerAnimation(`robot-${move.name.toLowerCase().replace(' ', '-')}`);
    setArenaActive(true);
    
    setTimeout(() => {
      setPlayerDancing(false);
      setPlayerAnimation('');
    }, move.duration * 1000);
  };

  const startOpponentDance = () => {
    const randomMove = danceMoves[Math.floor(Math.random() * danceMoves.length)];
    setOpponentDancing(true);
    setOpponentAnimation(`robot-${randomMove.name.toLowerCase().replace(' ', '-')}`);
    setArenaActive(true);
    
    setTimeout(() => {
      setOpponentDancing(false);
      setOpponentAnimation('');
    }, randomMove.duration * 1000);
  };

  // Auto opponent dancing
  useEffect(() => {
    const interval = setInterval(() => {
      if (!opponentDancing) {
        startOpponentDance();
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [opponentDancing]);

  // Loading and error handling
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Fallback timeout
  useEffect(() => {
    const fallbackTimer = setTimeout(() => {
      if (isLoading) {
        setUseFallback(true);
        setIsLoading(false);
      }
    }, 5000);

    return () => clearTimeout(fallbackTimer);
  }, [isLoading]);

  if (error) {
    return (
      <div className="w-full h-screen bg-black flex items-center justify-center">
        <div className="text-white text-center">
          <h2 className="text-2xl font-bold mb-4">3D Arena Error</h2>
          <p className="text-red-400 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  if (useFallback) {
    return (
      <FallbackArena 
        playerDancing={playerDancing}
        opponentDancing={opponentDancing}
        danceMoves={danceMoves}
        startPlayerDance={startPlayerDance}
      />
    );
  }

  return (
    <div className="w-full h-screen bg-black relative">
      {/* 3D Canvas */}
      <Canvas 
        camera={{ position: [0, 5, 10], fov: 60 }}
        style={{ width: '100%', height: '100%' }}
        gl={{ antialias: true, alpha: false }}
        onCreated={() => {
          setIsLoading(false);
        }}
        onError={(error) => {
          console.error('3D Scene Error:', error);
          setUseFallback(true);
          setIsLoading(false);
        }}
      >
        <ArenaLights isActive={arenaActive} />
        <DanceFloor isActive={arenaActive} />
        <DanceParticles isActive={arenaActive} />
        
        {/* Player Robot */}
        <Robot3D
          position={[-3, 0, 0]}
          color="#4a90e2"
          isDancing={playerDancing}
          danceAnimation={playerAnimation}
          name="Your Robot"
        />
        
        {/* Opponent Robot */}
        <Robot3D
          position={[3, 0, 0]}
          color="#e24a4a"
          isDancing={opponentDancing}
          danceAnimation={opponentAnimation}
          name="Dance Master"
        />
        
        <OrbitControls enablePan={true} enableZoom={true} enableRotate={true} />
      </Canvas>

      {/* UI Overlay */}
      <div className="absolute top-4 left-4 right-4 z-10">
        <div className="bg-black/70 backdrop-blur-sm rounded-lg p-4 text-white">
          <h2 className="text-2xl font-bold mb-4">🤖 3D Robot Dance Arena</h2>
          
          {/* Dance Moves */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            {danceMoves.map((move) => (
              <button
                key={move.id}
                onClick={() => startPlayerDance(move)}
                disabled={playerDancing}
                className={`p-2 rounded-lg text-sm font-medium transition-all ${
                  playerDancing 
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                <div className="text-lg mb-1">{move.emoji}</div>
                <div className="text-xs">{move.name}</div>
              </button>
            ))}
          </div>

          {/* Status */}
          <div className="flex justify-between items-center">
            <div className="text-sm">
              <span className="text-blue-400">Your Robot: </span>
              <span className={playerDancing ? 'text-green-400' : 'text-gray-400'}>
                {playerDancing ? 'Dancing!' : 'Ready'}
              </span>
            </div>
            <div className="text-sm">
              <span className="text-red-400">Dance Master: </span>
              <span className={opponentDancing ? 'text-green-400' : 'text-gray-400'}>
                {opponentDancing ? 'Dancing!' : 'Ready'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="absolute bottom-4 left-4 right-4 z-10">
        <div className="bg-black/70 backdrop-blur-sm rounded-lg p-4 text-white text-center">
          <p className="text-sm">
            🎮 Click dance moves to make your robot dance! 
            <br />
            🎯 Use mouse to rotate, zoom, and pan the 3D arena
          </p>
        </div>
      </div>

      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black z-20">
          <div className="text-white text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
            <p>Loading 3D Arena...</p>
            <p className="text-sm text-gray-400 mt-2">Initializing Three.js scene...</p>
            <p className="text-xs text-gray-500 mt-1">If this takes too long, we'll switch to 2D mode</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RobotDanceArena3D;